import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowRight, Maximize2 } from 'lucide-react'
import MarketingHeader from '../components/MarketingHeader'
import AppFooter from '../components/AppFooter'
import TemplateThumb, { sampleWordsFor } from '../components/invite/TemplateThumb'
import PaintedPreviewDialog from '../components/invite/PaintedPreviewDialog'
import TemplateFilterBar, { styleFilter } from '../components/invite/TemplateFilterBar'
import type { TemplateStyle } from '../components/invite/TemplateFilterBar'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '../contexts/LanguageContext'
import { DESIGNED_TEMPLATES, PAINTED_TEMPLATES, TEMPLATE_CATEGORIES, filterTemplates, getTemplate } from '../lib/inviteTemplates'
import type { TemplateCategory } from '../lib/inviteTemplates'
import '../components/landing/landing.css'

/**
 * Public gallery of every invitation template, by category, under one filter
 * row (search, style — the painted collection and its looks, or the classic
 * library — and category). Each card is the static thumbnail; "Preview" opens a painted
 * template's animated quick preview, or the real invitation wearing a classic
 * look (/templates/:id); "Use this template" starts the create wizard with it
 * preselected (?template=, plus the colours picked in the quick preview).
 * Marketing surface: strings are inline `isKa`.
 */
const TemplateGallery = () => {
  const { language } = useLanguage()
  const { user } = useAuth()
  const navigate = useNavigate()
  const isKa = language === 'ka'
  const lang = isKa ? 'ka' : 'en'

  const [style, setStyle] = useState<TemplateStyle>(PAINTED_TEMPLATES.length ? 'painted' : 'all')
  const [category, setCategory] = useState<TemplateCategory | 'all'>('all')
  const [query, setQuery] = useState('')
  const [quickId, setQuickId] = useState<string | null>(null)

  const visible = useMemo(
    () =>
      filterTemplates({ query, category, ...styleFilter(style) }).filter((tpl) => tpl.id !== 'auto'),
    [query, category, style],
  )
  const sections = useMemo(
    () =>
      TEMPLATE_CATEGORIES.map((c) => ({
        ...c,
        templates: visible.filter((tpl) => tpl.category === c.id),
      })).filter((c) => c.templates.length),
    [visible],
  )

  const startWithTemplate = (id: string, colours?: { accent?: string; background?: string }) => {
    const q = new URLSearchParams({ template: id })
    if (colours?.accent) q.set('accent', colours.accent.replace(/^#/, ''))
    if (colours?.background) q.set('bg', colours.background.replace(/^#/, ''))
    const to = `/events/create?${q.toString()}`
    navigate(user ? to : `/login?next=${encodeURIComponent(to)}`)
  }

  return (
    <div className="bg-story-gradient min-h-[100dvh] overflow-x-clip text-celebration-ink dark:text-[#f4f0ff]">
      <MarketingHeader />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0" aria-hidden="true">
          <div className="landing-blob landing-blob--a left-[-10%] top-[-20%] h-[40vh] w-[45vw] min-w-[300px]" />
          <div className="landing-blob landing-blob--b right-[-12%] top-[0%] h-[36vh] w-[40vw] min-w-[280px]" />
        </div>
        <div className="relative mx-auto max-w-6xl px-5 pb-8 pt-12 lg:pt-16">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#ff4d8d]">
            {isKa ? `${DESIGNED_TEMPLATES.length} დიზაინი` : `${DESIGNED_TEMPLATES.length} designs`}
          </p>
          <h1 className="mt-2 font-lynx text-[clamp(2rem,5vw,3.6rem)] font-semibold leading-tight tracking-tight">
            {isKa ? 'მოსაწვევის შაბლონები' : 'Invitation templates'}
          </h1>
          <p className="mt-3 max-w-[52ch] text-lg text-celebration-ink-2 dark:text-[#c3b8dd]">
            {isKa
              ? 'აირჩიე იერსახე, დაამატე შენი ფოტო და ფერები — და მოსაწვევი მზადაა. ყველა შაბლონი უფასოა.'
              : 'Pick a look, add your photo and colours — and the invitation is ready. Every template is free.'}
          </p>

          <div className="mt-8 max-w-3xl">
            <TemplateFilterBar
              query={query}
              onQuery={setQuery}
              style={style}
              onStyle={setStyle}
              category={category}
              onCategory={setCategory}
              appLang={lang}
              size="md"
              testPrefix="gallery"
            />
          </div>
        </div>
      </section>

      <section className="relative mx-auto max-w-6xl px-5 pb-20">
        {sections.map((section) => (
          <div key={section.id} className="mt-12 first:mt-4" data-testid={`gallery-section-${section.id}`}>
            <div className="mb-4 flex items-end justify-between gap-4">
              <h2 className="font-lynx text-[clamp(1.4rem,2.6vw,2rem)] font-semibold tracking-tight">
                {isKa ? section.label.ka : section.label.en}
              </h2>
              <span className="text-sm text-celebration-ink-2 dark:text-[#c3b8dd]">
                {section.templates.length}
              </span>
            </div>
            <div className="grid grid-cols-2 items-start gap-4 sm:grid-cols-3 lg:grid-cols-5">
              {section.templates.map((tpl) => {
                const previewHref = `/templates/${tpl.id}?noIntro=1&lang=${lang}`
                return (
                  <article
                    key={tpl.id}
                    className="group flex flex-col rounded-bubble border border-[#1b1033]/10 bg-white/70 p-2 shadow-celebrate-sm transition-shadow hover:shadow-celebrate-md dark:border-white/10 dark:bg-white/5"
                    data-testid={`gallery-template-${tpl.id}`}
                  >
                    {tpl.painted ? (
                      <button
                        type="button"
                        onClick={() => setQuickId(tpl.id)}
                        className="relative block w-full text-left"
                        aria-label={isKa ? `${tpl.label.ka} — გადახედვა` : `${tpl.label.en} — preview`}
                        data-testid={`gallery-preview-${tpl.id}`}
                      >
                        <TemplateThumb template={tpl} lang={lang} className="w-full" />
                        <span className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-full bg-[#1b1033]/70 px-2 py-1 text-[10px] font-medium text-white opacity-100 backdrop-blur transition-opacity sm:opacity-0 sm:group-hover:opacity-100">
                          <Maximize2 className="h-3 w-3" />
                          {isKa ? 'გადახედვა' : 'Preview'}
                        </span>
                      </button>
                    ) : (
                    <a href={previewHref} target="_blank" rel="noopener noreferrer" className="relative block" aria-label={isKa ? `${tpl.label.ka} — გადახედვა` : `${tpl.label.en} — preview`}>
                      <TemplateThumb template={tpl} lang={lang} className="w-full" />
                      <span className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-full bg-[#1b1033]/70 px-2 py-1 text-[10px] font-medium text-white opacity-0 backdrop-blur transition-opacity group-hover:opacity-100">
                        <Maximize2 className="h-3 w-3" />
                        {isKa ? 'გადახედვა' : 'Preview'}
                      </span>
                    </a>
                    )}
                    <h3 className="mt-2 truncate px-1 text-sm font-semibold">{isKa ? tpl.label.ka : tpl.label.en}</h3>
                    <p className="line-clamp-2 px-1 text-[11px] leading-snug text-celebration-ink-2 dark:text-[#c3b8dd]/80">
                      {isKa ? tpl.blurb.ka : tpl.blurb.en}
                    </p>
                    <button
                      type="button"
                      onClick={() => startWithTemplate(tpl.id)}
                      className="mt-2 inline-flex h-8 items-center justify-center gap-1 rounded-full bg-[#1b1033] px-3 text-[11px] font-semibold text-white transition-colors hover:bg-[#ff4d8d] dark:bg-white/10 dark:hover:bg-[#ff4d8d]"
                      data-testid={`gallery-use-${tpl.id}`}
                    >
                      {isKa ? 'ამ შაბლონით' : 'Use this template'}
                      <ArrowRight className="h-3 w-3" />
                    </button>
                  </article>
                )
              })}
            </div>
          </div>
        ))}
        {!sections.length && (
          <p className="py-16 text-center text-celebration-ink-2 dark:text-[#c3b8dd]" data-testid="gallery-empty">
            {isKa ? 'ასეთი შაბლონი ვერ მოიძებნა — სცადე სხვა სიტყვა.' : 'No template matches that — try another word.'}
          </p>
        )}

        <div className="mt-16 rounded-bubble-lg bg-celebration-ink p-8 text-center text-white dark:bg-[#1e1438] sm:p-12">
          <h2 className="font-lynx text-[clamp(1.6rem,3vw,2.4rem)] font-semibold tracking-tight">
            {isKa ? 'ვერ იპოვე შენი თემა?' : 'Can’t find your theme?'}
          </h2>
          <p className="mx-auto mt-3 max-w-[48ch] text-[#c3b8dd]">
            {isKa
              ? 'ნებისმიერი შაბლონის ფერი, ფონი და შრიფტი შეგიძლია შეცვალო — ან AI დიზაინერს სთხოვე ახალი ფონი.'
              : 'Every template’s colour, paper and type can be changed — or ask the AI designer for a fresh background.'}
          </p>
          <Link
            to={user ? '/events/create' : '/login'}
            className="mt-6 inline-flex h-11 items-center gap-2 rounded-full btn-gradient px-6 text-sm font-semibold text-white"
            data-testid="gallery-get-started"
          >
            {isKa ? 'დაიწყე' : 'Get started'}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <PaintedPreviewDialog
        templateId={quickId}
        list={visible.filter((tpl) => tpl.painted)}
        words={quickId ? sampleWordsFor(getTemplate(quickId), lang) : { title: '' }}
        lang={lang}
        appLang={lang}
        onClose={() => setQuickId(null)}
        onNavigate={setQuickId}
        onUse={({ template, accent, background }) => startWithTemplate(template, { accent, background })}
        useLabel={isKa ? 'ამ შაბლონით' : 'Use this template'}
      />

      <div className="relative overflow-hidden bg-celebration-ink dark:bg-[#0e081f]">
        <div className="relative">
          <AppFooter maxWidth="max-w-6xl" variant="onDark" bordered={false} />
        </div>
      </div>
    </div>
  )
}

export default TemplateGallery
